import { Injectable } from '@angular/core';
import {
  Http,
  XHRBackend,
  RequestOptions,
  Request,
  RequestOptionsArgs,
  Response,
  RequestMethod,
  Headers
} from '@angular/http';

import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';

import { StorageService } from './storage.service';
import { UrlFactoryService } from './url-factory.service';

export interface MonitoredHttpState {
  log: RequestEntry[];
  jwtStatus: JWT_STATUS;
  connectionStatus: CONNECTION_STATUS;
  rebootRequested: boolean;
  latestJWTRequest?: RequestEntry;
  latestNonJWTRequest?: RequestEntry;
  latestApiRequest?: RequestEntry;
}

export enum JWT_STATUS {
  UNKNOWN,
  NONE,
  VALID,
  INVALID
}

export enum CONNECTION_STATUS {
  UNKNOWN,
  CONNECTED,
  DISCONNECTED
}

export interface RequestEntry {
  url: string;
  method: string | RequestMethod;
  statusCode: number;
  statusText: string;
  time: number;
  error: boolean;
  jwtTransmitted: boolean;
  timestamp: number;
}

export const MONITORED_HTTP_INITIAL_STATE: MonitoredHttpState = {
  log: [],
  jwtStatus: JWT_STATUS.UNKNOWN,
  connectionStatus: CONNECTION_STATUS.UNKNOWN,
  rebootRequested: false
}

const STORE_NAME = 'MONITORED_HTTP';

const ACTIONS = {
  LOG: `${STORE_NAME}:LOG`,
  JWT_STATUS: `${STORE_NAME}:JWT_STATUS`,
  JWT_REQUEST: `${STORE_NAME}:JWT_REQUEST`,
  NON_JWT_REQUEST: `${STORE_NAME}:NON_JWT_REQUEST`,
  API_REQUEST: `${STORE_NAME}:API_REQUEST`,
  CONNECTION_STATUS: `${STORE_NAME}:CONNECTION_STATUS`,
  REBOOT_REQUESTED: `${STORE_NAME}:REBOOT_REQUESTED`
}

export { ACTIONS as MONITORED_HTTP_ACTIONS };
export const MONITORED_HTTP_STRINGS = Object.keys(ACTIONS).map(key => ACTIONS[key]);

export function monitoredHttpReducer(state: MonitoredHttpState = MONITORED_HTTP_INITIAL_STATE, {type, payload}) {

  // if (MONITORED_HTTP_STRINGS.indexOf(type) > -1) {
  //   console.debug('type: %s, payload: %O', type, payload);
  // }

  switch (type) {
    case ACTIONS.LOG:
      return Object.assign({}, state, { log: [...state.log, payload] });

    case ACTIONS.JWT_REQUEST:
      return Object.assign({}, state, { latestJWTRequest: payload });

    case ACTIONS.NON_JWT_REQUEST:
      return Object.assign({}, state, { latestNonJWTRequest: payload });

    case ACTIONS.API_REQUEST:
      return Object.assign({}, state, { latestApiRequest: payload });

    case ACTIONS.JWT_STATUS:
      return Object.assign({}, state, { jwtStatus: payload });

    case ACTIONS.CONNECTION_STATUS:
      let nu: MonitoredHttpState = Object.assign({}, state, { connectionStatus: payload });

      if (payload == CONNECTION_STATUS.CONNECTED) {
        nu.rebootRequested = false;
      }

      return nu;

    case ACTIONS.REBOOT_REQUESTED:
      return Object.assign({}, state, { rebootRequested: payload });

    default:
      return state;
  }
}

@Injectable()
export class MonitoredHttpService extends Http {

  private _latestRequest: Observable<RequestEntry>;
  private _latestJWTRequest: Observable<RequestEntry>;
  private _latestAPIRequest: Observable<RequestEntry>;

  constructor(
    public backend: XHRBackend,
    public defaultOptions: RequestOptions,
    private _store: Store<{
      monitoredHttpState: MonitoredHttpState
    }>,
    private _storage: StorageService,
    private _urlFactory: UrlFactoryService
  ) {
    super(backend, defaultOptions);

    this._latestRequest = this._store.select(s => s.monitoredHttpState)
      .filter(s => s.log && s.log.length > 0)
      .map(state => state.log[state.log.length - 1])
      .distinctUntilChanged()
      .share();

    this._latestRequest
      .filter(l => l.jwtTransmitted)
      .subscribe(latest => this._store.dispatch({
        type: ACTIONS.JWT_REQUEST,
        payload: latest
      }));

    this._latestRequest
      .filter(l => l.url && l.url.indexOf('/api/v1') > -1)
      .subscribe(latest => this._store.dispatch({
        type: ACTIONS.API_REQUEST,
        payload: latest
      }));

    this._latestAPIRequest = this._store.select(s => s.monitoredHttpState)
      .filter(s => !!s.latestApiRequest)
      .map(s => s.latestApiRequest);

    this._latestAPIRequest
      .distinctUntilChanged()
      .subscribe(latest => {
        let status: CONNECTION_STATUS;

        switch (latest.statusCode) {
          case undefined:
          case null:
          case 0:
          case 502:
            status = CONNECTION_STATUS.DISCONNECTED;
            break;

          default:
            status = CONNECTION_STATUS.CONNECTED;
        }

        this._store.dispatch({
          type: ACTIONS.CONNECTION_STATUS,
          payload: status
        });
      });

    this._latestRequest
      .filter(l => !l.jwtTransmitted)
      .subscribe(latest => this._store.dispatch({
        type: ACTIONS.NON_JWT_REQUEST,
        payload: latest
      }));

    this._latestJWTRequest = this._store.select(s => s.monitoredHttpState)
      .filter(s => !!s.latestJWTRequest)
      .map(s => s.latestJWTRequest);

    this._latestJWTRequest
      .distinctUntilChanged()
      .subscribe(latest => {
        let status: JWT_STATUS;

        switch (latest.statusCode) {
          case 200:
          case 400:
            status = JWT_STATUS.VALID;
            break;

          case 401:
            status = JWT_STATUS.INVALID;
            break;

          default:
            status = JWT_STATUS.UNKNOWN;
        }

        this._store.dispatch({
          type: ACTIONS.JWT_STATUS,
          payload: status
        })
      });

    const jwtStatus = this._store.select(s => s.monitoredHttpState)
      .map(s => s.jwtStatus)
      .distinctUntilChanged();

    // If jwt status is unknown, let's attempt to verify
    jwtStatus.filter(s => s == JWT_STATUS.UNKNOWN)
      .subscribe(s => {
        console.warn('WARN >> monitored-http.service.ts:157: JWT status is unknown, verifying...');
        this._verifyJWT();
      });

    jwtStatus.filter(s => s == JWT_STATUS.NONE)
      .subscribe(s => {
        console.warn('WARN >> monitored-http.service.ts:163: No JWT present.');
      });

    jwtStatus.filter(s => s == JWT_STATUS.INVALID)
      .subscribe(s => {
        console.warn('WARN >> monitored-http.service.ts:168: JWT is invalid.');
      });

    jwtStatus.filter(s => s == JWT_STATUS.VALID)
      .subscribe(s => {
        console.info('INFO >> monitored-http.service.ts:173: JWT is valid.');
      });

    const connectionStatus = this._store.select(s => s.monitoredHttpState)
      .map(s => s.connectionStatus)
      .distinctUntilChanged();

    connectionStatus.filter(s => s == CONNECTION_STATUS.UNKNOWN)
      .subscribe(s => {
        console.warn('WARN >> monitored-http.service.ts:219: API Connection status is unknown, verifying...');
        this._verifyConnection();
      });

    connectionStatus.filter(s => s == CONNECTION_STATUS.DISCONNECTED)
      .subscribe(s => {
        console.warn('WARN >> monitored-http.service.ts:225: API Connection is DOWN!');
      });

    connectionStatus.filter(s => s == CONNECTION_STATUS.CONNECTED)
      .subscribe(s => {
        console.info('INFO >> monitored-http.service.ts:230: API Connection is UP!');
      });
  }

  request(url: string | Request, options?: RequestOptionsArgs): Observable<Response> {

    let entry: RequestEntry = {
      url: undefined,
      method: undefined,
      statusCode: undefined,
      statusText: undefined,
      timestamp: Date.now(),
      time: undefined,
      error: undefined,
      jwtTransmitted: undefined
    };

    if (typeof url == 'string') {
      entry.url = url;
      entry.method = options.method || RequestMethod.Get;
      entry.jwtTransmitted = /JWT=/.test(url);
    } else {
      entry.url = url.url;
      entry.method = url.method;
      entry.jwtTransmitted = false;

      // If we haven't already attached authorization,
      // do so here. This helps us sniff JWT status.
      if (url.headers.get('Authorization') == null && !!this._storage.get('authToken')) {
        let JWT = this._storage.get('authToken');
        url.headers.append('Authorization', `Bearer ${JWT}`)
      }

      if (url.headers.get('Authorization') != null) {
        entry.jwtTransmitted = url.headers.get('Authorization').indexOf('Bearer ') == 0;
      }
    }

    const observable = super.request(url, options)

      // For success states
      .do(response => {
        entry.statusCode = response.status;
        entry.statusText = response.statusText;
        entry.error = false;
      })

      // For error states
      .catch((err: Response) => {
        entry.statusCode = err.status;
        entry.statusText = err.statusText;
        entry.error = true;

        return Observable.throw(err);
      })

      // After ALL requests, regardless of
      // success/error
      .finally(() => {
        entry.time = Date.now() - entry.timestamp;
        this._store.dispatch({
          type: ACTIONS.LOG,
          payload: entry
        })
      });

    return observable;
  }

  public setJwtStatus = (status: JWT_STATUS): void => this._store.dispatch({
    type: ACTIONS.JWT_STATUS,
    payload: status
  });

  public setConnectionStatus = (status: CONNECTION_STATUS): void => this._store.dispatch({
    type: ACTIONS.CONNECTION_STATUS,
    payload: status
  });

  public setRebootRequested = (requested: boolean): void => {
    this._store.dispatch({ type: ACTIONS.CONNECTION_STATUS, payload: CONNECTION_STATUS.DISCONNECTED });
    this._store.dispatch({ type: ACTIONS.REBOOT_REQUESTED, payload: true });
  };

  /**
   * Attempts to verify the status
   * of our JWT
   */
  private _verifyJWT = () => {
    const JWT = this._storage.get('authToken');

    if (!JWT) {
      this._store.dispatch({
        type: ACTIONS.JWT_STATUS,
        payload: JWT_STATUS.NONE
      });

    } else {
      this.request(new Request({
        url: this._urlFactory.getBrokerURL('/api/v1/common_name'),
        method: RequestMethod.Get,
        headers: new Headers({
          Authorization: `Bearer ${JWT}`,
          Accept: 'application/json'
        })
      })).subscribe();
    }
  }

  /**
   * Attempts to verify the status
   * of our API connection
   */
  private _verifyConnection = () => {
    this.request(new Request({
      url: this._urlFactory.getBrokerURL('/api/v1/common_name'),
      method: RequestMethod.Get,
      headers: new Headers({
        Accept: 'application/json'
      })
    })).subscribe();
  }

}



// WEBPACK FOOTER //
// ./~/@c4/core/src/app/services/monitored-http.service.ts